import {
  applyTheme,
  THEME_PREF_COOKIE_KEY,
  THEME_RESOLVED_COOKIE_KEY,
  LEGACY_THEME_STORAGE_KEY,
  parseThemePreference,
  resolveTheme,
  THEME_STORAGE_KEY,
  type ResolvedTheme,
  type ThemePreference
} from '~/utils/theme';

let mediaQuery: MediaQueryList | null = null;
let mediaListenerAttached = false;

export const useTheme = () => {
  const themePreference = useState<ThemePreference>('theme-preference', () => {
    if (process.server) {
      const cookiePreference = useCookie<ThemePreference | null>(THEME_PREF_COOKIE_KEY).value;
      return parseThemePreference(cookiePreference);
    }
    return 'system';
  });
  const prefersDark = useState<boolean>('prefers-dark', () => {
    if (process.server) {
      return useCookie<ResolvedTheme | null>(THEME_RESOLVED_COOKIE_KEY).value === 'dark';
    }
    return false;
  });
  const initialized = useState<boolean>('theme-initialized', () => false);

  const resolvedTheme = computed<ResolvedTheme>(() =>
    resolveTheme(themePreference.value, prefersDark.value)
  );

  const setTheme = (value: ThemePreference) => {
    themePreference.value = value;
  };

  if (process.client && !initialized.value) {
    const preferenceCookie = useCookie<ThemePreference>(THEME_PREF_COOKIE_KEY, {
      path: '/',
      sameSite: 'lax',
      maxAge: 60 * 60 * 24 * 365
    });
    const resolvedCookie = useCookie<ResolvedTheme>(THEME_RESOLVED_COOKIE_KEY, {
      path: '/',
      sameSite: 'lax',
      maxAge: 60 * 60 * 24 * 365
    });

    const stored = localStorage.getItem(THEME_STORAGE_KEY) ?? localStorage.getItem(LEGACY_THEME_STORAGE_KEY);
    themePreference.value = parseThemePreference(stored ?? themePreference.value);

    if (!mediaQuery) {
      mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    }
    prefersDark.value = mediaQuery.matches;

    if (!mediaListenerAttached) {
      mediaQuery.addEventListener('change', (event) => {
        prefersDark.value = event.matches;
      });
      mediaListenerAttached = true;
    }

    watch(
      themePreference,
      (value) => {
        localStorage.setItem(THEME_STORAGE_KEY, value);
        preferenceCookie.value = value;
      },
      { flush: 'sync' }
    );

    watch(
      resolvedTheme,
      (value) => {
        applyTheme(value);
        resolvedCookie.value = value;
      },
      { immediate: true }
    );

    initialized.value = true;
  }

  return {
    themePreference,
    resolvedTheme,
    setTheme
  };
};
